import { faRobot } from "@fortawesome/free-solid-svg-icons/faRobot";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";

import { AtiraIcon } from "../../../../components/AtiraIcon";
import { Button } from "../../../../components/Button";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { Conversation } from "../../../../model/integration/dto/meta/MetaConversation";
import { WAConversationHandler } from "../../../../model/integration/types/whatsapp/WAConversationHandler.enum";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Rounded } from "../../../../theme/Rounded";
import { Spacing } from "../../../../theme/Spacing";
import { AtiraToast } from "../../../../utils/AtiraToast";

const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.12); opacity: 0.75; }
  100% { transform: scale(1); opacity: 1; }
`;

const Banner = styled(Flex)`
  position: sticky;
  bottom: 0.5rem;
  width: 100%;
  margin: 0.5rem 0;
  align-items: center;
  gap: ${Spacing.m};
  padding: ${Spacing.m};
  border-top: 1px solid ${({ theme }) => theme.main};
  background: ${({ theme }) =>
    `linear-gradient(120deg, ${theme.lightSub} 0%, ${theme.white} 100%)`};
`;

const AgentAvatar = styled(Flex)`
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: ${Rounded.circle};
  background-color: ${({ theme }) => theme.lightMain};
  animation: ${pulse} 2.4s ease-in-out infinite;
`;

const TakeOverButton = styled(Button)`
  margin: 0;
  flex-shrink: 0;
`;

type Props = {
  chat: Conversation;
};

/** Replaces the composer while the AI agent owns the conversation. */
export const WAAgentBanner: React.FC<Props> = ({ chat }) => {
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );

  const agentName =
    integration?.config?.aiAgent?.name ||
    t("integration.whatsapp.meta.aiAgent.defaultName");

  const onTakeOver = async () => {
    try {
      setLoading(true);

      await dispatch(
        integrationActions.setWhatsappMetaConversationHandler({
          userId,
          integrationId: integration!._id,
          conversationId: chat._id,
          handledBy: WAConversationHandler.HUMAN,
        }),
      ).unwrap();

      AtiraToast.success(
        t("integration.whatsapp.meta.aiAgent.takeOver.success"),
      );
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Banner>
      <AgentAvatar>
        <AtiraIcon icon={faRobot} color="white" size="lg" />
      </AgentAvatar>

      <Flex flexDirection="column" flex={1} gap="xs">
        <Text fontWeight="bold" color="main">
          {t("integration.whatsapp.meta.aiAgent.banner.title", {
            name: agentName,
          })}
        </Text>
        <Text fontSize="s" color="darkerSub">
          {t("integration.whatsapp.meta.aiAgent.banner.description")}
        </Text>
      </Flex>

      <TakeOverButton onClick={onTakeOver} loading={loading} disabled={loading}>
        {t("integration.whatsapp.meta.aiAgent.banner.takeOver")}
      </TakeOverButton>
    </Banner>
  );
};
